import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import AttendanceFilters from "../../components/attendance/AttendanceFilters";
import DailyTeamGrid    from "../../components/attendance/DailyTeamGrid";
import RangeSummary     from "../../components/attendance/RangeSummary";
import ExceptionsQueue  from "../../components/attendance/ExceptionsQueue";
import MemberDrawer     from "../../components/attendance/MemberDrawer";
import RotaSchedulerPage from "../../pages/rota/RotaSchedulerPage";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

async function apiFetch(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function TeamAttendancePage() {
  const [managerId, setManagerId] = useState(null);
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      if (u?.id) setManagerId(u.id);
    } catch {}
  }, []);

  const [topTab, setTopTab]   = useState("attendance"); // attendance | rota
  const [view, setView]       = useState("daily");
  const [filters, setFilters] = useState({
    date: new Date().toISOString().slice(0, 10),
    from: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10),
    to:   new Date().toISOString().slice(0, 10),
    status: "ALL",
    onlyAnomalies: false,
    search: "",
  });

  const [dailyRows,  setDailyRows]  = useState([]);
  const [rangeItems, setRangeItems] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [selected,   setSelected]   = useState(null);

  useEffect(() => {
    if (!managerId) return;
    if (topTab !== "attendance") return;
    let active = true;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        if (view === "daily") {
          const data = await apiFetch(
            `/api/manager/attendance/daily?manager_id=${managerId}&date=${filters.date}`
          );
          if (!active) return;
          setDailyRows(Array.isArray(data) ? data : data?.rows || []);

        } else if (view === "range") {
          const data = await apiFetch(
            `/api/manager/attendance/range?manager_id=${managerId}&from=${filters.from}&to=${filters.to}`
          );
          if (!active) return;
          setRangeItems(Array.isArray(data) ? data : data?.items || []);

        } else {
          const data = await apiFetch(
            `/api/manager/attendance/exceptions?manager_id=${managerId}&from=${filters.from}&to=${filters.to}`
          );
          if (!active) return;
          setExceptions(Array.isArray(data) ? data : data?.items || []);
        }
      } catch (e) {
        if (!active) return;
        setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["attendance_checkin", "attendance_checkout", "attendance_regularise"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      active = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [managerId, topTab, view, filters.date, filters.from, filters.to]);

  // KPIs derived from daily rows
  const kpis = useMemo(() => {
    if (view !== "daily") return null;
    return {
      present:         dailyRows.filter(r => ["PRESENT", "WFH"].includes(r.status)).length,
      absent:          dailyRows.filter(r => r.status === "ABSENT").length,
      late:            dailyRows.filter(r => r.late > 0).length,
      wfh:             dailyRows.filter(r => r.status === "WFH").length,
      exceptionsCount: dailyRows.filter(r => (r.flags || []).length > 0).length,
    };
  }, [dailyRows, view]);

  // Client-side filtering
  const visibleRows = useMemo(() => {
    let rows = [...dailyRows];
    if (filters.status !== "ALL")   rows = rows.filter(r => r.status === filters.status);
    if (filters.onlyAnomalies)      rows = rows.filter(r => (r.flags || []).length > 0 || r.late > 0);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      rows = rows.filter(r =>
        r.name?.toLowerCase().includes(q) || r.role?.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [dailyRows, filters]);

  const handleApprove = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/manager/attendance/exceptions/${id}/approve`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      setExceptions(prev => prev.map(x => x.id === id ? { ...x, status: "APPROVED" } : x));
    } catch {
      setExceptions(prev => prev.map(x => x.id === id ? { ...x, status: "APPROVED" } : x));
    }
  };

  const handleReject = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/manager/attendance/exceptions/${id}/reject`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      setExceptions(prev => prev.map(x => x.id === id ? { ...x, status: "REJECTED" } : x));
    } catch {
      setExceptions(prev => prev.map(x => x.id === id ? { ...x, status: "REJECTED" } : x));
    }
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Team Attendance</h1>
          <p className="text-white/40 text-sm mt-0.5">Monitor and manage your team's attendance</p>
        </div>

        {/* Top tabs */}
        <div className="flex gap-2 border-b border-white/5">
          {[["attendance", "Attendance"], ["rota", "Rota"]].map(([k, l]) => (
            <button key={k} onClick={() => setTopTab(k)}
              className={`px-4 py-2 text-sm transition-colors -mb-px border-b-2 ${
                topTab === k ? "text-[#00ff90] border-[#00ff90]" : "text-white/50 border-transparent hover:text-white/80"
              }`}>
              {l}
            </button>
          ))}
        </div>

        {topTab === "rota" && (
          <div className={`${card} p-5`}>
            <RotaSchedulerPage embedded={true} />
          </div>
        )}

        {topTab === "attendance" && (
        <>
        {/* Filters */}
        <AttendanceFilters
          view={view}
          setView={setView}
          filters={filters}
          setFilters={setFilters}
          kpis={kpis}
        />

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
            ⚠ {error}
          </div>
        )}

        {/* Content panel */}
        <div className={`${card} p-5`}>
          {view === "daily" && (
            <DailyTeamGrid
              loading={loading}
              rows={visibleRows}
              onRowClick={(row) => setSelected(row)}
            />
          )}
          {view === "range" && (
            <RangeSummary loading={loading} items={rangeItems} />
          )}
          {view === "exceptions" && (
            <ExceptionsQueue
              loading={loading}
              items={exceptions}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )}
        </div>
        </>
        )}

      </div>

      {/* Member drawer */}
      {selected && (
        <MemberDrawer
          member={selected}
          onClose={() => setSelected(null)}
          onRegularise={(member) => {
            setSelected(null);
            // Could open regularisation modal here if needed
          }}
        />
      )}
    </MessengerLayout>
  );
}